/**
 * How well a key matches a search query. Higher is better.
 */
const Ranking = {
  NoMatch: 0,
  WordStartsWith: 1,
  Contains: 2,
  StartsWith: 3,
  Equal: 4,
} as const

interface RankingInfo {
  keyIndex: number
  rank: number
}

/**
 * Filters items by the query and sorts the best matches first.
 *
 * Each item has one or more keys, most important first, like an ID and then a
 * label. A match on a more important key always comes first, so any ID match
 * beats an exact label match. For the same key, the higher {@link Ranking}
 * comes first. Ties keep their original order.
 */
export function rankedSearch<T>(
  items: readonly T[],
  query: string,
  getKeys: (item: T) => (string | null | undefined)[],
): T[] {
  const normalizedQuery = normalizeSearchText(query)
  if (!normalizedQuery) {
    return items.slice()
  }

  return items
    .map((item) => ({
      item,
      rankingInfo: getRankingInfo(getKeys(item), normalizedQuery),
    }))
    .filter(
      (x): x is { item: T; rankingInfo: RankingInfo } => x.rankingInfo !== null,
    )
    .sort(
      (a, b) =>
        a.rankingInfo.keyIndex - b.rankingInfo.keyIndex ||
        b.rankingInfo.rank - a.rankingInfo.rank,
    )
    .map(({ item }) => item)
}

function getRankingInfo(
  keys: (string | null | undefined)[],
  normalizedQuery: string,
): RankingInfo | null {
  for (const [keyIndex, key] of keys.entries()) {
    const rank = getMatchRanking(key ?? '', normalizedQuery)
    if (rank !== Ranking.NoMatch) {
      return { keyIndex, rank }
    }
  }
  return null
}

function getMatchRanking(key: string, normalizedQuery: string): number {
  const normalizedKey = normalizeSearchText(key)
  if (normalizedKey === normalizedQuery) {
    return Ranking.Equal
  }
  if (normalizedKey.startsWith(normalizedQuery)) {
    return Ranking.StartsWith
  }
  if (normalizedKey.includes(normalizedQuery)) {
    return Ranking.Contains
  }
  // The loosest match is checked last so a closer match wins.
  if (matchesWordPrefixes(normalizedKey, normalizedQuery)) {
    return Ranking.WordStartsWith
  }
  return Ranking.NoMatch
}

/**
 * Checks whether each query word starts a word in the text, in order. Text
 * words can be skipped but not reused, so `bu name` matches both
 * `business name` and `business entity name`, while `name bu` and `bu bus`
 * match neither.
 */
function matchesWordPrefixes(
  normalizedText: string,
  normalizedQuery: string,
): boolean {
  const textWords = normalizedText.split(' ')
  let searchFromIndex = 0
  for (const queryWord of normalizedQuery.split(' ')) {
    const matchIndex = textWords.findIndex(
      (textWord, index) =>
        index >= searchFromIndex && textWord.startsWith(queryWord),
    )
    if (matchIndex === -1) {
      return false
    }
    searchFromIndex = matchIndex + 1
  }
  return true
}

/**
 * Lowercases and treats runs of spaces, underscores and dashes as one space,
 * so `business name` matches `BUSINESS_NAME`.
 */
function normalizeSearchText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[\s_-]+/g, ' ')
    .trim()
}
